import type { Navigation, NavigationItem, Page } from '~/types';

export function getNavItemUrl(item: NavigationItem): string | undefined {
  if (item.type === 'page' && (item.page as Page)) {
    return (item.page as Page)?.permalink ?? undefined;
  } else if (item.type === 'url' && item.url) {
    return item.url as string;
  }

  return undefined;
}

export interface PublishedNavigationItem extends Omit<NavigationItem, 'children'> {
  children: PublishedNavigationItem[];
}

export interface PublishedNavigation extends Omit<Navigation, 'items'> {
  items: PublishedNavigationItem[];
}

/** Drop unresolved, unpublished and nested-as-root items even from cached data. */
export function getPublishedNavigation(value?: Navigation): PublishedNavigation | undefined {
  if (!value || value.status !== 'published') return undefined;

  function items(values: NavigationItem['children'], roots = false): PublishedNavigationItem[] {
    return (values || [])
      .filter((item): item is NavigationItem => typeof item === 'object' && item !== null)
      .filter((item) => item.status === 'published' && (!roots || !item.parent))
      .map((item) => {
        const children = items(item.children);
        return { ...item, children, has_children: item.has_children === true && children.length > 0 };
      })
      .filter((item) => item.has_children || !!getNavItemUrl(item))
      .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0) || String(a.id).localeCompare(String(b.id)));
  }

  return { ...value, items: items(value.items, true) };
}
